import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { GROUP_TYPES, GROUP_VISIBILITY, SKILL_LEVELS } from '../constants';
import JoinRequestModal from '../components/JoinRequestModal';
import GroupDetailsModal from '../components/GroupDetailsModal';
import { supabase } from '../config/supabaseClient';
import { useAuth } from '../contexts/AuthContext';

export default function JoinGroupsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    skillLevel: null,
    gender: null,
    visibility: null
  });
  
  // Join request modal state
  const [showJoinRequestModal, setShowJoinRequestModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);

  // Group details modal state
  const [showGroupDetailsModal, setShowGroupDetailsModal] = useState(false);
  const [selectedGroupForDetails, setSelectedGroupForDetails] = useState(null);

  // Groups state
  const [groups, setGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch groups from Supabase
  useEffect(() => {
    const fetchGroups = async () => {
      setIsLoading(true);
      try {
        console.log('Fetching groups for user:', user?.id);
        
        // Get all groups with their member counts and user membership status
        const { data: allGroups, error: groupsError } = await supabase
          .rpc('get_available_groups', { user_id: user.id });

        if (groupsError) {
          console.error('Error fetching groups:', groupsError);
          throw groupsError;
        }

        console.log('All groups found:', allGroups);

        // Filter out groups where user is already a member or admin
        const filteredGroups = allGroups.filter(group => 
          !group.is_member && 
          group.admin_id !== user.id
        );

        console.log('Filtered groups:', filteredGroups);

        setGroups(filteredGroups.map(group => ({
          ...group,
          memberCount: group.member_count || 0
        })));
      } catch (err) {
        console.error('Error fetching groups:', err);
        setError('Failed to fetch groups.');
        setGroups([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchGroups();
  }, [user?.id]);

  // Filter button component
  const FilterButton = ({ label, value, currentValue, onClick }) => (
    <button
      type="button"
      onClick={() => onClick(value)}
      className={`px-4 py-3 rounded-lg border text-base ${
        currentValue === value
          ? 'bg-primary/5 border-primary text-primary'
          : 'border-gray-300 text-textMed hover:bg-gray-50'
      }`}
    >
      {label}
    </button>
  );

  // Handle filter changes
  const handleFilterChange = (type, value) => {
    setFilters(prev => ({
      ...prev,
      [type]: prev[type] === value ? null : value
    }));
  };

  // Filter groups based on search and filters
  const filteredGroups = groups.filter(group => {
    const matchesSearch = group.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesSkill = !filters.skillLevel || group.skill_level === filters.skillLevel;
    const matchesGender = !filters.gender || group.gender === filters.gender;
    const matchesVisibility = !filters.visibility || group.visibility === filters.visibility;
    return matchesSearch && matchesSkill && matchesGender && matchesVisibility;
  });

  // Handle join/request actions
  const handleJoinClick = async (group) => {
    try {
      if (!user) {
        navigate('/login');
        return;
      }

      console.log('Attempting to join group:', {
        group_id: group.id,
        group_name: group.name,
        visibility: group.visibility,
        user_id: user.id
      });

      if (group.visibility === GROUP_VISIBILITY.PUBLIC) {
        const { error: joinError } = await supabase
          .from('group_members')
          .insert([{
            group_id: group.id,
            user_id: user.id,
            role: 'member'
          }]);

        if (joinError) {
          console.error('Error joining group:', joinError);
          if (joinError.code === '23505') {
            setError('You are already a member of this group');
          } else {
            throw joinError;
          }
          return;
        }

        navigate('/');
      } else {
        console.log('Opening join request modal for private group');
        setSelectedGroup(group);
        setShowJoinRequestModal(true);
      }
    } catch (error) {
      console.error('Error joining group:', error);
      setError('Failed to join group. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F8F8]">
      <Header />
      
      <main className="pb-8">
        {/* Back Button and Title Section */}
        <div className="sticky top-0 bg-white border-b z-10">
          <div className="container mx-auto px-4 py-3">
            <div className="flex items-center">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center text-textMed hover:text-textDark transition-colors p-2 -ml-2"
              >
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className="h-6 w-6" 
                  viewBox="0 0 20 20" 
                  fill="currentColor"
                >
                  <path 
                    fillRule="evenodd" 
                    d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L4.414 9H17a1 1 0 110 2H4.414l5.293 5.293a1 1 0 010 1.414z" 
                    clipRule="evenodd" 
                  />
                </svg>
              </button>
              <h1 className="text-xl font-bold text-textDark ml-2">Join Group</h1>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="container mx-auto px-4 py-4">
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            {/* Search Bar */}
            <div className="p-4 border-b">
              <input
                type="text"
                placeholder="Search groups..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              />
            </div>

            {/* Filters */}
            <div className="p-4 space-y-6">
              {/* Filter Header */}
              <div className="border-b pb-3">
                <h3 className="text-lg font-semibold text-textDark">Apply filters to find group</h3>
              </div>

              {/* Skill Level Filter */}
              <div>
                <label className="block text-base font-semibold mb-3">NTRP Rating</label>
                <div className="grid grid-cols-4 gap-2">
                  {Object.entries(SKILL_LEVELS).map(([key, value]) => (
                    <FilterButton
                      key={key}
                      label={value}
                      value={value}
                      currentValue={filters.skillLevel}
                      onClick={(value) => handleFilterChange('skillLevel', value)}
                    />
                  ))}
                </div>
              </div>

              {/* Gender Filter */}
              <div>
                <label className="block text-base font-semibold mb-3">Gender</label>
                <div className="grid grid-cols-3 gap-2">
                  {Object.entries(GROUP_TYPES).map(([key, value]) => (
                    <FilterButton
                      key={key}
                      label={key.charAt(0) + key.slice(1).toLowerCase()}
                      value={value}
                      currentValue={filters.gender}
                      onClick={(value) => handleFilterChange('gender', value)}
                    />
                  ))}
                </div>
              </div>

              {/* Visibility Filter */}
              <div>
                <label className="block text-base font-semibold mb-3">Visibility</label>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(GROUP_VISIBILITY).map(([key, value]) => (
                    <FilterButton
                      key={key}
                      label={key.charAt(0) + key.slice(1).toLowerCase()}
                      value={value}
                      currentValue={filters.visibility}
                      onClick={(value) => handleFilterChange('visibility', value)}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Groups List */}
        <div className="container mx-auto px-4">
          <h2 className="text-lg font-bold text-textDark mb-4">Available Groups</h2>
          <div className="space-y-4">
            {filteredGroups.map(group => (
              <div
                key={group.id}
                className="bg-white rounded-lg shadow-sm p-4 space-y-4"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg font-semibold text-textDark">{group.name}</h3>
                    <p className="text-sm text-textMed">{group.memberCount} members</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm ${
                    group.visibility === GROUP_VISIBILITY.PUBLIC
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {group.visibility.charAt(0).toUpperCase() + group.visibility.slice(1)}
                  </span>
                </div>

                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-primary/5 text-primary rounded-full text-sm">
                    {group.gender.charAt(0).toUpperCase() + group.gender.slice(1)}
                  </span>
                  <span className="px-3 py-1 bg-primary/5 text-primary rounded-full text-sm">
                    NTRP {group.skill_level}
                  </span>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setSelectedGroupForDetails(group);
                      setShowGroupDetailsModal(true);
                    }}
                    className="flex-1 py-3 px-4 bg-gray-100 text-textDark rounded-lg font-medium hover:bg-gray-200 transition-colors"
                  >
                    Learn More
                  </button>
                  <button
                    onClick={() => handleJoinClick(group)}
                    className={`flex-1 py-3 rounded-lg font-semibold text-base ${
                      group.visibility === GROUP_VISIBILITY.PUBLIC
                        ? 'bg-primary text-white hover:bg-primaryDark'
                        : 'border-2 border-primary text-primary hover:bg-primary/5'
                    }`}
                  >
                    {group.visibility === GROUP_VISIBILITY.PUBLIC ? 'Join Group' : 'Request to Join'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* Join Request Modal */}
      {selectedGroup && (
        <JoinRequestModal
          show={showJoinRequestModal}
          onClose={() => {
            setShowJoinRequestModal(false);
            setSelectedGroup(null);
          }}
          group={selectedGroup}
        />
      )}

      {/* Group Details Modal */}
      {selectedGroupForDetails && (
        <GroupDetailsModal
          show={showGroupDetailsModal}
          onClose={() => {
            setShowGroupDetailsModal(false);
            setSelectedGroupForDetails(null);
          }}
          group={selectedGroupForDetails}
        />
      )}
    </div>
  );
} 